import { useEffect, useState } from "react";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { Tables } from "~/types/supabase";

type ManualFactura = Tables<"manual_facturas"> & {
  client_name?: string;
  client_tax_id?: string;
  client_seller?: string;
  client_sales_condition?: string;
  client_insured_amount?: number;
};

export const useManualFacturas = () => {
  const [manualFacturas, setManualFacturas] = useState<ManualFactura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();

  // Function to fetch manual facturas with client information
  const fetchManualFacturas = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, fetch all manual_facturas with client information
      const { data: manualFacturasData, error: facturaError } = await supabase
        .from("manual_facturas")
        .select(
          `
          *,
          clients!inner (
            name,
            tax_id,
            insured_amount,
            sales_condition:sales_condition_id (
              name,
              maximum_days
            ),
            assigned_seller!inner (
              name,
              lastname
            )
          )
        `
        )
        .order("created_at", { ascending: false });

      if (facturaError) {
        throw facturaError;
      }

      // Transform the data to include client information
      const facturas =
        manualFacturasData?.map((factura) => ({
          ...factura,
          client_name: (factura.clients as any)?.name || "",
          client_tax_id: (factura.clients as any)?.tax_id || "",
          client_seller:
            (factura.clients as any)?.assigned_seller?.name +
              " " +
              (factura.clients as any)?.assigned_seller?.lastname || "",
          client_sales_condition:
            (factura.clients as any)?.sales_condition?.name || "",
          client_insured_amount: (factura.clients as any)?.insured_amount || 0,
        })) || [];

      setManualFacturas(facturas);
    } catch (err) {
      console.error("Error fetching manual facturas:", err);
      setError(
        "No se pudieron cargar las cuentas por cobrar. Por favor, intenta nuevamente."
      );
      toast({
        title: "Error",
        description:
          "No se pudieron cargar las cuentas por cobrar. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh the manual facturas
  const refreshManualFacturas = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Filter facturas based on search term
  const filteredFacturas = manualFacturas.filter((factura) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const clientName = factura.client_name?.toLowerCase() || "";
    const clientTaxId = factura.client_tax_id?.toLowerCase() || "";
    const docNumber = factura.doc_number?.toLowerCase() || "";
    const description = factura.description?.toLowerCase() || "";
    const clientRut = factura.client_rut?.toLowerCase() || "";

    return (
      clientName.includes(searchLower) ||
      clientTaxId.includes(searchLower) ||
      docNumber.includes(searchLower) ||
      description.includes(searchLower) ||
      clientRut.includes(searchLower)
    );
  });

  // Fetch manual facturas when refreshKey changes
  useEffect(() => {
    fetchManualFacturas();
  }, [refreshKey]);

  return {
    manualFacturas: filteredFacturas,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    refreshManualFacturas,
  };
};
